/**
 * AlphaScoutDashboard — Phase 6 (Current / Past / Picks / Backtest)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Target, RefreshCw, Database, TrendingUp, Loader2, Activity, CheckCircle, Clock,
  ChevronDown, ChevronUp, Wrench,
} from 'lucide-react';
import PicksTable from './PicksTable';
import FactorRadar from './FactorRadar';
import BacktestExplorer from './BacktestExplorer';
import CurrentPicks from './CurrentPicks';
import PastPicks from './PastPicks';
import PerformanceTab from './PerformanceTab';
import NewsLegend from './NewsLegend';
import ModelPortfolioCard from './ModelPortfolioCard';
import PITCoverageCard from './PITCoverageCard';  // SESSION 147j+4: VH PIT fundamentals coverage
import { timeAgo } from './utils';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Session 147j+16ae — Surface "when did picks last refresh" prominently
// so users don't wonder "why are the same stocks showing"  when a
// rebalance ran but produced the same top-K (the scoring genuinely
// converged on the same names).
const RebalanceFreshnessBadge = ({ picks }) => {
  const rebalancedAt = picks?.rebalanced_at;
  if (!rebalancedAt) {
    return (
      <span
        className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground"
        data-testid="rebalance-freshness-none"
      >
        no rebalance yet
      </span>
    );
  }
  const diffMin = (Date.now() - new Date(rebalancedAt).getTime()) / 60000;
  const isFresh = diffMin < 60 * 24;           // < 1 day
  const isStale = diffMin > 60 * 24 * 2;       // > 2 days
  const tone = isStale
    ? 'border-amber-500/40 bg-amber-500/10 text-amber-300'
    : isFresh
      ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300'
      : 'border-border bg-surface text-muted-foreground';
  const addedN = (picks?.added_symbols || []).length;
  const removedN = (picks?.removed_symbols || []).length;
  const noChange = addedN === 0 && removedN === 0;
  return (
    <div
      className={`inline-flex items-center gap-2 px-2 py-1 border font-mono text-[10px] uppercase tracking-wider ${tone}`}
      data-testid="rebalance-freshness-badge"
      title={`Rebalanced at ${new Date(rebalancedAt).toLocaleString('en-IN')}`}
    >
      <Clock className="w-3 h-3" />
      <span data-testid="rebalance-freshness-time">
        Rebalanced {timeAgo(rebalancedAt)}
      </span>
      {picks?.rebalance_type && (
        <span className="opacity-70 hidden sm:inline">
          · {picks.rebalance_type}
        </span>
      )}
      {!noChange && (
        <span data-testid="rebalance-freshness-delta" className="opacity-80">
          · {addedN > 0 && <span className="text-emerald-300">+{addedN}</span>}
          {addedN > 0 && removedN > 0 && ' '}
          {removedN > 0 && <span className="text-red-300">-{removedN}</span>}
        </span>
      )}
      {noChange && (
        <span
          className="opacity-70 hidden sm:inline"
          data-testid="rebalance-freshness-no-change"
        >
          · no change (scoring converged on same names)
        </span>
      )}
    </div>
  );
};

const AlphaScoutDashboard = ({ profile = 'largecap', readOnly = false }) => {
  const { authFetch } = useAuth();
  const [pipelineStatus, setPipelineStatus] = useState(null);
  const [scoringStatus, setScoringStatus] = useState(null);
  const [picks, setPicks] = useState(null);
  const [selectedPick, setSelectedPick] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionState, setActionState] = useState({ fetching: false, scoring: false, rebalancing: false });
  const [view, setView] = useState('current'); // 'current' | 'past' | 'performance' | 'picks' | 'backtest'
  const [adminOpen, setAdminOpen] = useState(false);

  // Session 147j — profile-aware dashboard. `profile` prop selects which
  // portfolio (largecap / midsmall / valuehunter) is rendered. All axios
  // calls append ?profile=<name> so backend partitions reads by collection.
  const isHunter = profile === 'midsmall';
  const isValueHunter = profile === 'valuehunter';
  const accentColor = isValueHunter ? 'text-violet-400'
    : isHunter ? 'text-amber-400' : 'text-accent';
  const profileQ = `profile=${encodeURIComponent(profile)}`;

  const loadAll = useCallback(async () => {
    try {
      const [rPipe, rScore, rPicks] = await Promise.all([
        authFetch(`${API}/api/alphascout/status?${profileQ}`),
        authFetch(`${API}/api/alphascout/scoring/status?${profileQ}`),
        authFetch(`${API}/api/alphascout/picks?${profileQ}`),
      ]);
      setPipelineStatus(await rPipe.json());
      setScoringStatus(await rScore.json());
      const dPicks = await rPicks.json();
      setPicks(dPicks);
      if (dPicks?.picks?.length && !selectedPick) setSelectedPick(dPicks.picks[0]);
    } catch (e) {
      console.error('[AlphaScout] loadAll failed', e);
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authFetch, profileQ]);

  useEffect(() => {
    loadAll();
    const interval = setInterval(loadAll, 15000);
    return () => clearInterval(interval);
  }, [loadAll]);

  const triggerFetch = async (force = false) => {
    setActionState((s) => ({ ...s, fetching: true }));
    try {
      await authFetch(`${API}/api/alphascout/fetch/trigger?force=${force}&${profileQ}`, { method: 'POST' });
    } finally {
      setActionState((s) => ({ ...s, fetching: false }));
      setTimeout(loadAll, 1500);
    }
  };

  const runScoring = async () => {
    setActionState((s) => ({ ...s, scoring: true }));
    try {
      await authFetch(`${API}/api/alphascout/scoring/run?${profileQ}`, { method: 'POST' });
    } finally { setActionState((s) => ({ ...s, scoring: false })); loadAll(); }
  };

  const rebalance = async (force = false) => {
    setActionState((s) => ({ ...s, rebalancing: true }));
    try {
      await authFetch(`${API}/api/alphascout/picks/rebalance?force=${force}&${profileQ}`, { method: 'POST' });
    } finally { setActionState((s) => ({ ...s, rebalancing: false })); loadAll(); }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  }

  const isFetchRunning = pipelineStatus?.is_running || actionState.fetching;

  return (
    <div
      className={`space-y-4 ${isHunter ? 'border-l-2 border-amber-500/30 pl-4 pr-1 -ml-1' : ''} ${isValueHunter ? 'border-l-2 border-violet-500/40 pl-4 pr-1 -ml-1' : ''}`}
      data-testid={isValueHunter ? 'alphascout-valuehunter-dashboard' : isHunter ? 'alphascout-hunter-dashboard' : 'alphascout-dashboard'}
    >
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground flex items-center gap-2">
            <Target className={`w-6 h-6 ${accentColor}`} />
            {isValueHunter ? 'AlphaScout — ValueHunter'
              : isHunter ? 'AlphaScout — Hunter'
                : 'AlphaScout'}
            {isHunter && (
              <span className="px-2 py-0.5 text-[10px] font-mono uppercase border border-amber-500/40 bg-amber-500/10 text-amber-400 rounded">
                Mid + Small Cap
              </span>
            )}
            {isValueHunter && (
              <span className="px-2 py-0.5 text-[10px] font-mono uppercase border border-violet-500/40 bg-violet-500/10 text-violet-400 rounded">
                Deep Value · Nifty 500
              </span>
            )}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {isValueHunter
              ? 'Deep-value contrarian engine · Full Nifty 500 (~482 names) · Multiplicative composite (Value ∩ Quality ∩ Contrarian) · 10-stock portfolio · 15% SL / 30% target · Monthly rebalance · 6-12 month hold'
              : isHunter
                ? 'Mid + small cap discovery (Nifty 500 ex-Nifty 200) · Liquidity-gated ₹5 Cr/day · Momentum-tilted · 12% SL / 25% target'
                : 'AI stock picker · NIFTY 200 universe (deduped 184) · 5-factor composite · Sharpe 1.44 · CAGR 34.8% (7Y) / 28.7% (10Y)'}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <TabButton active={view === 'current'} onClick={() => setView('current')} testId="tab-current">Current</TabButton>
          <TabButton active={view === 'past'} onClick={() => setView('past')} testId="tab-past">Past</TabButton>
          <TabButton active={view === 'performance'} onClick={() => setView('performance')} testId="tab-performance">Performance</TabButton>
          <TabButton active={view === 'picks'} onClick={() => setView('picks')} testId="tab-picks">Factor Breakdown</TabButton>
          {/* Session 147j+16bo Ship 2 — Backtest is admin-only (requires
              POST + heavy compute). Hidden in read-only mode. */}
          {!readOnly && (
            <TabButton active={view === 'backtest'} onClick={() => setView('backtest')} testId="tab-backtest">Backtest</TabButton>
          )}
        </div>
      </div>

      {/* Status strip (always visible) */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatusCard icon={Database} label="Coverage" value={`${pipelineStatus?.today_stocks || 0}/${scoringStatus?.universe_size || pipelineStatus?.universe_size || 0}`} subvalue={`${pipelineStatus?.coverage_pct || 0}%`} isActive={isFetchRunning} />
        <StatusCard icon={CheckCircle} label="Scored Today" value={scoringStatus?.scored_today || 0} subvalue={`${scoringStatus?.qualified_today || 0} qualified`} />
        <StatusCard icon={Clock} label="Last Scoring" value={timeAgo(scoringStatus?.last_scoring_run)} />
        <StatusCard icon={Activity} label="Current Picks" value={picks?.picks_count || 0} subvalue={`hysteresis ±${scoringStatus?.hysteresis_threshold || 10}`} />
      </div>

      {/* Session 147j+4 — ValueHunter PIT fundamentals coverage card */}
      {isValueHunter && <PITCoverageCard profile={profile} readOnly={readOnly} />}

      {/* Main view */}
      {view === 'current' && (
        <div className="space-y-4">
          {/* Session 142e — AlgoGreeks Model Portfolio header card. Largecap-only. */}
          {!isHunter && !isValueHunter && <ModelPortfolioCard />}
          <CurrentPicks profile={profile} readOnly={readOnly} />
        </div>
      )}
      {view === 'past' && <PastPicks profile={profile} />}
      {view === 'performance' && <PerformanceTab profile={profile} />}

      {view === 'picks' && (
        <div className="space-y-4">
          {/* Session 142e — AlgoGreeks Model Portfolio running performance card */}
          {!isHunter && !isValueHunter && <ModelPortfolioCard />}

          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-3">
              <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
                Stable Top Picks with Factor Breakdown
              </h3>
              <NewsLegend />
            </div>
            <RebalanceFreshnessBadge picks={picks} />
          </div>

          {/* News gate banner — shows any picks that were blocked this cycle */}
          {picks?.news_blocked?.length > 0 && (
            <div
              className="bg-red-500/5 border border-red-500/40 p-3 flex items-start gap-3"
              data-testid="news-blocked-banner"
            >
              <span className="shrink-0 text-red-400 text-lg leading-none mt-0.5">⚠</span>
              <div className="text-xs text-foreground">
                <div className="font-bold text-red-400 mb-1">
                  {picks.news_blocked.length} pick{picks.news_blocked.length > 1 ? 's' : ''} blocked by news gate
                </div>
                <div className="text-muted-foreground space-y-0.5">
                  {picks.news_blocked.map((b) => (
                    <div key={b.symbol} className="flex gap-2">
                      <span className="font-mono font-bold text-foreground min-w-[80px]">{b.symbol}</span>
                      <span className="truncate">{b.top_event || 'Red-severity event detected'}</span>
                    </div>
                  ))}
                </div>
                <div className="text-[10px] text-muted-foreground mt-1 italic">
                  These stocks qualified on score but were excluded due to recent SEBI orders, rating downgrades, CXO resignations, or similar material events.
                </div>
              </div>
            </div>
          )}

          <PicksTable
            picks={picks?.picks || []}
            selectedSymbol={selectedPick?.symbol}
            onRowClick={(p) => setSelectedPick(p)}
          />
          <div>
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
                Factor Radar + AI Rationale
              </h3>
              {selectedPick && (
                <span className="text-[11px] text-muted-foreground">
                  Click any row above to switch
                </span>
              )}
            </div>
            <FactorRadar pick={selectedPick} />
          </div>
        </div>
      )}

      {view === 'backtest' && !readOnly && <BacktestExplorer profile={profile} />}

      {/* Admin tools (collapsible) — completely hidden in read-only mode
          (Session 147j+16bo Ship 2). Read-only users see no POST controls. */}
      {!readOnly && (
        <div className="bg-surface border border-border overflow-hidden">
        <button
          type="button"
          onClick={() => setAdminOpen(!adminOpen)}
          className="w-full px-3 py-2 flex items-center justify-between hover:bg-surface-highlight transition-colors"
          data-testid="admin-tools-toggle"
        >
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground font-semibold">
            <Wrench className="w-3.5 h-3.5" />
            Admin Tools
          </div>
          {adminOpen ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
        </button>
        {adminOpen && (
          <div className="border-t border-border p-3">
            <p className="text-[11px] text-muted-foreground mb-3">
              These actions run automatically via the nightly cron (4:30 PM IST). Use manually only to force-test changes.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <ActionButton icon={Database} label="Populate Universe" onClick={() => triggerFetch(false)} loading={isFetchRunning} loadingLabel="Fetching..." testId="alphascout-fetch-btn" />
              <ActionButton icon={TrendingUp} label="Run Scoring" onClick={runScoring} loading={actionState.scoring} loadingLabel="Scoring..." disabled={(pipelineStatus?.today_stocks || 0) < 10} testId="alphascout-score-btn" />
              <ActionButton icon={Target} label="Rebalance Picks" onClick={() => rebalance(false)} loading={actionState.rebalancing} loadingLabel="Rebalancing..." disabled={!scoringStatus?.qualified_today} testId="alphascout-rebalance-btn" />
              <button onClick={loadAll} className="ml-auto text-xs text-muted-foreground hover:text-foreground flex items-center gap-1" data-testid="alphascout-refresh-btn">
                <RefreshCw className="w-3 h-3" /> Refresh
              </button>
            </div>
          </div>
        )}
        </div>
      )}
    </div>
  );
};

const TabButton = ({ active, onClick, children, testId }) => (
  <button type="button" onClick={onClick} className={`px-4 py-2 text-sm font-semibold transition-colors border ${active ? 'bg-accent/10 text-accent border-accent' : 'bg-surface border-border text-muted-foreground hover:text-foreground'}`} data-testid={testId}>
    {children}
  </button>
);

const StatusCard = ({ icon: Icon, label, value, subvalue, isActive }) => (
  <div className="bg-surface border border-border p-3 relative">
    {isActive && <span className="absolute top-2 right-2 w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />}
    <div className="flex items-center gap-2 text-muted-foreground text-[10px] uppercase tracking-wider font-semibold mb-1.5">
      <Icon className="w-3 h-3" />{label}
    </div>
    <div className="text-xl font-bold font-mono text-foreground">{value}</div>
    {subvalue && <div className="text-[11px] text-muted-foreground mt-0.5">{subvalue}</div>}
  </div>
);

const ActionButton = ({ icon: Icon, label, onClick, loading, loadingLabel, disabled, testId }) => (
  <button type="button" onClick={onClick} disabled={loading || disabled} className="px-3 py-2 bg-surface-highlight border border-border text-sm font-medium flex items-center gap-2 hover:border-accent/50 hover:text-accent disabled:opacity-40 disabled:cursor-not-allowed transition-colors" data-testid={testId}>
    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" />}
    {loading ? loadingLabel : label}
  </button>
);

export default AlphaScoutDashboard;
